import React from 'react';
import './index.scss';
import { useTranslation } from 'react-i18next';
import {
  Dropdown,
  Menu,
  Icon,
  Button,
  DropdownItemProps,
  Modal,
} from 'semantic-ui/semantic-ui-imports';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-semantic-toasts';
import { useAuth } from '../../contexts/auth';
import { useData } from '../../contexts/data';
import { PromptToSignInContext } from '../../contexts/promptToSignIn';
import LanguageSelector from '../LanguageSelector';
import CreateOrUpdateProject from '../CreateOrUpdateProject';
import { createResult, updateResult, getResult } from '../../services/results';
import { getProjects, getProject } from '../../services/projects';

// Fields to strip from data when saving (regenerated from DB on load)
const DATA_FIELDS_TO_STRIP = [
  'acoustic_attenuations',
  'aggregate_picture',
  'category',
  'description',
  'min_qv',
  'name',
  'power_supply',
  'stub_placements_left',
  'stub_placements_right',
  'technical_specification_categories',
  'technical_specifications',
  'three_d_model_link',
  'water_reheater',
];

// Sanitize payload before saving to reduce size and avoid stale data
// availableAddons is used to filter out addons not valid for current aggregate
const sanitizePayloadForSave = (payload: any, availableAddons?: any[]): any => {
  const sanitized = { ...payload };

  // Convert inputs.addons from full objects to just IDs, ensuring uniqueness
  // Also filter to only include addons valid for the current aggregate
  if (sanitized.inputs?.addons && Array.isArray(sanitized.inputs.addons)) {
    let addonIds = sanitized.inputs.addons.map((addon: any) =>
      typeof addon === 'object' ? addon.id : addon
    );

    // Filter to only valid addons if availableAddons is provided
    if (availableAddons?.length) {
      const validIds = new Set(availableAddons.map((a) => a.id));
      addonIds = addonIds.filter((id: number) => validIds.has(id));
    }

    // Deduplicate IDs
    const uniqueAddonIds = [...new Set(addonIds)];
    sanitized.inputs = {
      ...sanitized.inputs,
      addons: uniqueAddonIds,
    };
  }

  // Strip unnecessary fields from data (will be re-fetched from DB)
  if (sanitized.data) {
    const strippedData = { ...sanitized.data };
    DATA_FIELDS_TO_STRIP.forEach((field) => {
      delete strippedData[field];
    });
    sanitized.data = strippedData;
  }

  return sanitized;
};

const isCalculationPage = (pathname: string): boolean => {
  // Check if we're on a calculation flow page
  if (pathname === '/') return true;
  if (pathname.startsWith('/configure')) return true;
  if (pathname.startsWith('/addons')) return true;
  // /results (both calculation flow and saved project results)
  if (pathname === '/results' || pathname.startsWith('/results/')) return true;
  if (pathname.match(/\/projects\/\d+\/results\//)) return true;
  return false;
};

// SaveModal component - must be defined before NavBar which uses it
const SaveModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: (saved?: boolean) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    projects,
    setProjects,
    activeUserInputs,
    activeAggregate,
    selectedResultId,
    setSelectedResultId,
    alternativeTo,
    setAlternativeTo,
    markAsSaved,
    availableAddons,
  } = useData();
  const { isAuthenticated } = useAuth();
  const { promptToSignIn } = React.useContext(PromptToSignInContext);

  const [name, setName] = React.useState('');
  const [projectId, setProjectId] = React.useState<string | null>(null);
  const [projectLocked, setProjectLocked] = React.useState(false);
  const [lockedProjectName, setLockedProjectName] = React.useState<string>('');
  const [originalResultName, setOriginalResultName] = React.useState<string>(
    ''
  );
  const [loading, setLoading] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [showProjectModal, setShowProjectModal] = React.useState(false);

  // Load projects on mount
  React.useEffect(() => {
    const loadProjects = async () => {
      if (isAuthenticated && (!projects || projects.length === 0)) {
        try {
          const res = await getProjects();
          if (res?.results?.length) {
            setProjects(res.results);
          }
        } catch (err) {
          // Ignore
        }
      }
    };
    loadProjects();
  }, [isAuthenticated]);

  // Pre-fill project from original result when creating alternative
  React.useEffect(() => {
    const loadOriginalResultProject = async () => {
      if (alternativeTo && !selectedResultId) {
        try {
          const originalResult = await getResult(alternativeTo);
          if (originalResult?.project) {
            const projectIdStr = String(originalResult.project);
            setProjectId(projectIdStr);
            setProjectLocked(true);
            setOriginalResultName(originalResult.name || '');
            // Fetch project details to get name
            const projectDetails = await getProject(projectIdStr);
            if (projectDetails?.name) {
              setLockedProjectName(projectDetails.name);
            }
          }
        } catch (err) {
          // Ignore - user can enter values manually
        }
      } else {
        setProjectLocked(false);
        setLockedProjectName('');
        setOriginalResultName('');
      }
    };
    loadOriginalResultProject();
  }, [alternativeTo, selectedResultId]);

  // Pre-fill if editing existing result
  React.useEffect(() => {
    const loadExistingResult = async () => {
      if (selectedResultId) {
        try {
          const existingResult = await getResult(selectedResultId);
          if (existingResult) {
            setName(existingResult.name || '');
            if (existingResult.project) {
              setProjectId(String(existingResult.project));
            }
          }
        } catch (err) {
          // Ignore - user can enter values manually
        }
      }
    };
    loadExistingResult();
  }, [selectedResultId]);

  const isValid = !!name && !!projectId;

  const handleSave = async () => {
    // Prevent duplicate submissions
    if (loading) return;

    if (!isAuthenticated) {
      promptToSignIn();
      return;
    }

    setSubmitted(true);
    if (!isValid) return;

    setLoading(true);

    // Determine status based on current page
    const { pathname } = location;
    let status: 'draft' | 'selected' | 'calculated' = 'draft';
    if (
      pathname === '/results' ||
      pathname.startsWith('/results/') ||
      pathname.includes('/results/')
    ) {
      status = 'calculated';
    } else if (
      pathname.startsWith('/configure') ||
      pathname.startsWith('/addons')
    ) {
      status = 'selected';
    }

    // Build payload - support drafts (no aggregate)
    // When saving from the product selection page (/), always save as draft
    // (aggregate selection is not "committed" until clicking Continue)
    const isProductSelectionPage = pathname === '/';
    const payload: any = {
      name,
      project: projectId,
      inputs: activeUserInputs,
      status,
    };

    // Only include aggregate if we have one AND not on product selection page
    if (activeAggregate && !isProductSelectionPage) {
      payload.aggregate = activeAggregate.id;
      payload.data = activeAggregate;
    }

    // Include alternative_to for partner alternatives
    if (alternativeTo) {
      payload.alternative_to = alternativeTo;
    }

    try {
      let result;
      const sanitizedPayload = sanitizePayloadForSave(payload, availableAddons);

      if (selectedResultId) {
        sanitizedPayload.id = selectedResultId;
        result = await updateResult(sanitizedPayload);
      } else {
        result = await createResult(sanitizedPayload);
      }

      if (result) {
        // Update tracking state
        setSelectedResultId(String(result.id));
        setAlternativeTo(null); // Clear after save
        markAsSaved();

        toast({
          type: 'success',
          title: t('saveToProject.projectWasSaved'),
          time: 4000,
          size: 'small',
        });

        onClose(true);

        // Navigate to bookmarkable URL when saving from results page
        if (pathname === '/results' || pathname.startsWith('/results/')) {
          navigate(`/projects/${result.project}/results/${result.id}`);
        }
      }
    } catch (err) {
      toast({
        type: 'error',
        title: t('common.somethingWentWrong'),
        time: 4000,
        size: 'small',
      });
    }

    setLoading(false);
  };

  return (
    <>
      <Modal open={open} onClose={() => onClose()} size="small">
        <Modal.Header>{t('saveToProject.title')}</Modal.Header>
        <Modal.Content>
          <div className="ui form">
            <p>{t('saveToProject.descriptions')}</p>
            {originalResultName && (
              <div className="ui info message" style={{ marginBottom: '1rem' }}>
                <p>
                  {t('resultList.alternativeTo', { name: originalResultName })}
                </p>
              </div>
            )}
            <div className="field required">
              <label>{t('saveToProject.projectLabel')}</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {projectLocked ? (
                  <input
                    type="text"
                    value={lockedProjectName}
                    disabled
                    style={{ flex: 1 }}
                  />
                ) : (
                  <>
                    <select
                      className="ui dropdown"
                      value={projectId || ''}
                      onChange={(e) => setProjectId(e.target.value || null)}
                      style={{ flex: 1 }}
                    >
                      <option value="">
                        {t('saveToProject.projectPlaceholder')}
                      </option>
                      {projects?.map((p: any) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      icon="plus"
                      onClick={() => setShowProjectModal(true)}
                    />
                  </>
                )}
              </div>
              {submitted && !projectId && (
                <div className="ui pointing red basic label">
                  {t('saveToProject.projectError')}
                </div>
              )}
            </div>
            <div className="field required">
              <label>{t('saveToProject.nameLabel')}</label>
              <input
                type="text"
                placeholder={t('common.inputWrite')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {submitted && !name && (
                <div className="ui pointing red basic label">
                  {t('saveToProject.nameError')}
                </div>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => onClose()} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            primary
            loading={loading}
            disabled={loading}
            onClick={handleSave}
          >
            {t('common.save')}
          </Button>
        </Modal.Actions>
      </Modal>

      <CreateOrUpdateProject
        show={showProjectModal}
        onHide={(obj: any) => {
          if (obj) {
            setProjects(projects?.length ? [...projects, obj] : [obj]);
            setProjectId(obj.id);
          }
          setShowProjectModal(false);
        }}
      />
    </>
  );
};

const NavBar = () => {
  const { t } = useTranslation();
  const { isAuthenticated, logout, isPartner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    resetStorageData,
    toggleSideMenu,
    hasUnsavedChanges,
    startNewCalculation,
    activeUserInputs,
    activeAggregate,
    selectedResultId,
    alternativeTo,
    setAlternativeTo,
    markAsSaved,
    availableAddons,
  } = useData();
  const [userMenuItems, setUserMenuItems] = React.useState<DropdownItemProps[]>(
    []
  );
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [showSaveModal, setShowSaveModal] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  // Determine if we're on a calculation page
  const onCalculationPage = isCalculationPage(location.pathname);

  // Show Save button: on calculation pages, authenticated, and has inputs
  const hasInputs =
    activeUserInputs && Object.keys(activeUserInputs).length > 0;
  const showSaveButton = isAuthenticated && onCalculationPage && hasInputs;

  // Save is primary CTA when there are unsaved changes
  const saveIsPrimary = showSaveButton && hasUnsavedChanges;

  React.useEffect(() => {
    if (isAuthenticated) {
      const items: DropdownItemProps[] = [
        {
          key: 'profile',
          value: 'profile',
          text: t('navBar.profile'),
          onClick: () => navigate('/profile'),
        },
      ];

      if (isPartner) {
        items.push({
          key: 'offerRequests',
          value: 'offerRequests',
          text: t('navBar.offerRequests'),
          onClick: () => navigate('/offerrequests'),
        });
      }

      items.push({
        key: 'mypage',
        value: 'mypage',
        text: t('navBar.mypage'),
        onClick: () => navigate('/mypage'),
      });

      items.push({
        key: 'logout',
        value: 'logout',
        text: t('navBar.signout'),
        onClick: () => {
          logout();
          navigate('/login');
        },
      });

      setUserMenuItems(items);
    }
  }, [location, isAuthenticated, isPartner]);

  const doNewCalculation = () => {
    setShowConfirmModal(false);
    startNewCalculation();
    resetStorageData();
    navigate('/');
  };

  const handleNewCalculation = () => {
    if (hasUnsavedChanges) {
      setShowConfirmModal(true);
    } else {
      doNewCalculation();
    }
  };

  // Direct save for existing results (no modal)
  const handleDirectSave = async () => {
    if (isSaving || !selectedResultId) return;

    setIsSaving(true);

    // Determine status based on current page
    const { pathname } = location;
    let status: 'draft' | 'selected' | 'calculated' = 'draft';
    if (
      pathname === '/results' ||
      pathname.startsWith('/results/') ||
      pathname.includes('/results/')
    ) {
      status = 'calculated';
    } else if (
      pathname.startsWith('/configure') ||
      pathname.startsWith('/addons')
    ) {
      status = 'selected';
    }

    // Build payload
    const isProductSelectionPage = pathname === '/';
    const payload: any = {
      id: selectedResultId,
      inputs: activeUserInputs,
      status,
    };

    // Only include aggregate if we have one AND not on product selection page
    if (activeAggregate && !isProductSelectionPage) {
      payload.aggregate = activeAggregate.id;
      payload.data = activeAggregate;
    }

    // Include alternative_to for partner alternatives
    if (alternativeTo) {
      payload.alternative_to = alternativeTo;
    }

    try {
      const sanitizedPayload = sanitizePayloadForSave(payload, availableAddons);
      const result = await updateResult(sanitizedPayload);

      if (result) {
        setAlternativeTo(null);
        markAsSaved();

        toast({
          type: 'success',
          title: t('saveToProject.projectWasSaved'),
          time: 4000,
          size: 'small',
        });

        // Navigate to bookmarkable URL when saving from results page
        if (pathname === '/results' || pathname.startsWith('/results/')) {
          navigate(`/projects/${result.project}/results/${result.id}`);
        }
      }
    } catch (err) {
      toast({
        type: 'error',
        title: t('common.somethingWentWrong'),
        time: 4000,
        size: 'small',
      });
    }

    setIsSaving(false);
  };

  const handleSaveClick = () => {
    // For existing results: direct save without modal
    if (selectedResultId) {
      handleDirectSave();
    } else {
      // For new results: show modal to enter project + name
      setShowSaveModal(true);
    }
  };

  return (
    <Menu secondary className="nav-bar">
      {location && location.pathname.match('results') && (
        <div className="nav-bar__menu">
          <Icon
            name="bars"
            onClick={() => {
              toggleSideMenu();
            }}
          />
        </div>
      )}
      <div className="nav-bar__logo">
        <img src="/nilan_logo_without_payoff.svg" alt="nilan logo" />
      </div>

      <Menu.Menu className="nav-bar__right" position="right">
        {showSaveButton && (
          <Button
            className="nav-bar__right__button"
            primary={saveIsPrimary}
            basic={!saveIsPrimary}
            color="blue"
            disabled={!hasUnsavedChanges || isSaving}
            loading={isSaving}
            onClick={handleSaveClick}
          >
            <Icon name="save" />
            {selectedResultId ? t('common.updateDraft') : t('common.save')}
          </Button>
        )}
        {!!isAuthenticated && location?.pathname !== '/' && (
          <Button
            className="nav-bar__right__button"
            primary={!saveIsPrimary}
            basic={saveIsPrimary}
            color="blue"
            onClick={handleNewCalculation}
          >
            <Icon name="edit" />
            {t('navBar.newCalc')}
          </Button>
        )}
        <LanguageSelector />
        {!!isAuthenticated && (
          <Dropdown
            selection
            icon="user"
            className="nav-bar__right__dropdown"
            options={userMenuItems}
          ></Dropdown>
        )}
        {!isAuthenticated && (
          <Menu.Item className="nav-bar__signin">
            <Button basic size="small" onClick={() => navigate('/login')}>
              {t('navBar.signin')}
            </Button>
          </Menu.Item>
        )}
      </Menu.Menu>

      {/* Confirmation modal for New Calculation with unsaved changes */}
      <Modal
        size="tiny"
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
      >
        <Modal.Header>{t('navBar.newCalc')}</Modal.Header>
        <Modal.Content>
          <p>{t('navBar.unsavedChangesWarning')}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowConfirmModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button primary onClick={doNewCalculation}>
            {t('navBar.discardAndContinue')}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Save modal - we'll import SaveModal component */}
      {showSaveModal && (
        <SaveModal
          open={showSaveModal}
          onClose={() => setShowSaveModal(false)}
        />
      )}
    </Menu>
  );
};

export default NavBar;
